import { FC, Fragment, useState, useCallback, useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { BottomSheet } from "@widgets/layout";
import {
  StatusMessage,
  CommonForm,
  TextareaInputGroup,
  DefaultIcon,
} from "@shared/ui";
import { useAutoCloseByStatus, useModalScrollLock } from "@shared/utils";
import IconMessage from "/svg/message.svg";
import { FORM_FIELDS } from "@shared/constants";
import { useFormFocus } from "@shared/hooks";
import * as Yup from "yup";
import { FormikProps } from "formik";
import { useEditMode } from "@features/profile/model";

import styles from "./EditDescriptionBottomSheets.module.scss";
import { useDecodeHtml } from "@shared/utils/decodeHTML";

interface IEditDescriptionBottomSheetsProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: (
    description: string
  ) => Promise<boolean> | boolean | Promise<void> | void;
  initialDescription: string;
  errorMessage?: string | null;
  onErrorReset?: () => void;
}

const descriptionValidationSchema = Yup.object().shape({
  description: Yup.string()
    .required("Расскажите о себе")
    .max(300, "Максимум 300 символов"),
});

export const EditDescriptionBottomSheets: FC<
  IEditDescriptionBottomSheetsProps
> = (props) => {
  const {
    isOpen,
    onClose,
    onSave,
    initialDescription,
    errorMessage,
    onErrorReset,
  } = props;

  const [status, setStatus] = useState<"success" | "error" | null>(null);
  const { focusedFields, handleFieldFocus, handleFieldBlur } = useFormFocus({
    description: false,
  });
  const { closeBottomSheet } = useEditMode();
  const formikRef = useRef<FormikProps<{ description: string }> | null>(null);
  const descriptionRef = useRef(initialDescription);

  useModalScrollLock(isOpen);
  useAutoCloseByStatus(status, setStatus, onClose);

  const handleSubmit = async (values: { description: string }) => {
    try {
      if (onSave) {
        const result = await onSave(values.description);

        if (result === false) {
        } else {
          setStatus("success");
          if (errorMessage && onErrorReset) {
            onErrorReset();
          }
        }
      }
    } catch (error) {
      console.error("Failed to update description:", error);
      setStatus("error");
    }
  };

  const handleButtonClick = useCallback(async () => {
    if (formikRef.current) {
      await formikRef.current.submitForm();
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setStatus(null);
    }
  }, [isOpen]);

  const handleDirectClose = () => {
    if (errorMessage && onErrorReset) {
      onErrorReset();
    }
    onClose();
    closeBottomSheet();
  };

  useEffect(() => {
    if (!isOpen || status) return;

    const tg = window.Telegram.WebApp;
    tg.MainButton.setText("Сохранить");
    tg.MainButton.show();

    tg.MainButton.onClick(handleButtonClick);

    return () => {
      tg.MainButton.offClick(handleButtonClick);
      tg.MainButton.hide();
    };
  }, [handleButtonClick, isOpen, status]);

  return (
    <AnimatePresence>
      {isOpen && (
        <Fragment>
          <BottomSheet isOpen={isOpen} onClose={handleDirectClose}>
            {!status && (
              <button
                className={styles.editDescriptionCloseButton}
                onClick={handleDirectClose}
              />
            )}
            <div className={styles.editDescriptionContent}>
              <AnimatePresence mode="wait">
                {status ? (
                  <StatusMessage
                    status={status}
                    successType={"Описание успешно обновлено"}
                  />
                ) : (
                  <motion.div
                    key="form"
                    className={styles.editDescriptionContainer}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <h2 className={styles.editDescriptionTitle}>
                      Редактировать описание
                    </h2>
                    <CommonForm
                      initialValues={{ description: initialDescription }}
                      onSubmit={handleSubmit}
                      validationSchema={descriptionValidationSchema}
                    >
                      {(formik) => {
                        useEffect(() => {
                          formikRef.current = formik;
                          descriptionRef.current = formik.values.description;
                        }, [formik.values]);

                        return (
                          <div className={styles.editDescriptionInputs}>
                            {errorMessage && (
                              <p className={styles.errorMessage}>
                                {errorMessage}
                              </p>
                            )}
                            <TextareaInputGroup
                              placeholder="Расскажите о себе"
                              label="О себе"
                              name={FORM_FIELDS.DESCRIPTION}
                              value={useDecodeHtml(formik.values.description)}
                              onChange={formik.handleChange}
                              onBlur={(e) => {
                                formik.handleBlur(e);
                                handleFieldBlur(FORM_FIELDS.DESCRIPTION);
                              }}
                              disabled={formik.isSubmitting}
                              minRows={3}
                              autosize
                              maxLength={300}
                              icon={() => <DefaultIcon src={IconMessage} />}
                              error={
                                formik.touched.description &&
                                formik.errors.description
                              }
                              isFocused={focusedFields.description}
                              setIsFocused={(focus) =>
                                handleFieldFocus(FORM_FIELDS.DESCRIPTION, focus)
                              }
                            />
                          </div>
                        );
                      }}
                    </CommonForm>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </BottomSheet>
        </Fragment>
      )}
    </AnimatePresence>
  );
};
